// Booklet after $mol_book2 (Decision booklet-layout), POC markup: main#book (horizontal scroll-snap scroller) >
// div#row (the pages; drag mode scales this box, not the scroller) > sections + a trailing .fill.
// An optional page that appears is brought into view; on phones the first column is centred on first load.
import { type CSSProperties, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icon";

/** `overlay`: floating controls beside the pips (the board's filter pill), in #book's timeline scope, outside #row. */
type Props = {
  bookRef: RefObject<HTMLElement | null>;
  mobile: boolean;
  ready: boolean;
  overlay?: ReactNode;
  children: ReactNode;
};
export function Book({
  bookRef,
  mobile,
  ready,
  overlay,
  children
}: Props) {
  const {
    t
  } = useTranslation();
  const first = useRef(true);
  const phone = useRef(mobile);
  phone.current = mobile;
  useLayoutEffect(() => {
    const book = bookRef.current;
    if (!book || !ready || !first.current) return;
    first.current = false;
    const c = book.querySelector<HTMLElement>("#row")?.children[1] as HTMLElement | undefined;
    if (mobile && c) book.scrollTo({
      left: c.offsetLeft - (book.clientWidth - c.offsetWidth) / 2,
      behavior: "instant"
    });
  }, [ready, mobile, bookRef]);

  // Page pips (book.css .pips): one per page, in order; the list follows #row's children. Each page gets its own
  // view timeline --p<i> (named here, scoped on #book): its pip follows it in CSS, nothing else drives it.
  // A page that appears (a view opened from the nav or the session pill, a page pushed by a row, a panel) is brought
  // into view, the first of several (a view's first column): on phones snapped to its start, on desktop scrolled
  // just far enough to show its right edge. Read off the DOM, so whatever opens a page gets it focused.
  const [pips, setPips] = useState<string[]>([]);
  useEffect(() => {
    const book_0 = bookRef.current;
    const row = book_0?.querySelector<HTMLElement>("#row");
    if (!book_0 || !row) return;
    let seen: Set<string> | null = null;
    const focus = (el: HTMLElement) => {
      const left = phone.current ? el.offsetLeft : el.offsetLeft + el.offsetWidth - book_0.clientWidth;
      if (phone.current || left > book_0.scrollLeft) book_0.scrollTo({
        left: Math.max(0, left),
        behavior: "smooth"
      });
    };
    const sync = () => {
      const pages = [...row.querySelectorAll<HTMLElement>(":scope > section:not(.fill)")];
      const fresh = seen && pages.find(s => !s.classList.contains("nav") && !seen!.has(s.dataset.page ?? ""));
      seen = new Set(pages.map(s_0 => s_0.dataset.page ?? ""));
      if (fresh) focus(fresh);
      pages.forEach((s_1, i) => {
        s_1.style.setProperty("view-timeline-name", s_1.classList.contains("nav") ? `--nav, --p${i}` : `--p${i}`);
        s_1.style.setProperty("view-timeline-axis", s_1.classList.contains("nav") ? "x, x" : "x");
      });
      book_0.style.setProperty("timeline-scope", ["--nav", ...pages.map((_, i_0) => `--p${i_0}`)].join(", "));
      const keys = pages.map(s_2 => s_2.dataset.page ?? "");
      setPips(old => old.join(" ") === keys.join(" ") ? old : keys);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(row, {
      childList: true
    });
    return () => mo.disconnect();
  }, [bookRef]);
  // Only where CSS has no scroll-driven ranges (Firefox), once per scroll frame (attributes change only on a flip;
  // CSS transitions them): a pip is lit (data-on) while its page is more than half in view, #book.nav-off once the
  // nav is mostly out (the < button).
  useEffect(() => {
    const book_1 = bookRef.current;
    if (!book_1 || CSS.supports("(animation-range: exit 0%) and (timeline-scope: --a)")) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const x0 = book_1.getBoundingClientRect().left;
      const w = book_1.clientWidth;
      const pips_0 = book_1.querySelectorAll<HTMLElement>(".pip");
      book_1.querySelectorAll<HTMLElement>("#row > section:not(.fill)").forEach((s_3, i_1) => {
        const r = s_3.getBoundingClientRect();
        const seen_0 = (Math.min(r.right - x0, w) - Math.max(r.left - x0, 0)) / Math.min(r.width, w);
        if (s_3.classList.contains("nav") && book_1.classList.contains("nav-off") !== seen_0 < 0.4) book_1.classList.toggle("nav-off");
        const p = pips_0[i_1];
        if (p && p.hasAttribute("data-on") !== seen_0 > 0.5) p.toggleAttribute("data-on");
      });
    };
    const onScroll = () => {
      frame ||= requestAnimationFrame(check);
    };
    check();
    book_1.addEventListener("scroll", onScroll, {
      passive: true
    });
    addEventListener("resize", onScroll);
    return () => {
      book_1.removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [bookRef, pips]);
  // Pips only when the book scrolls sideways: with every page on screen (a wide desktop) each one would be lit and
  // say nothing. .pips[data-fits] hides them; sizes, not scroll, decide (the pages' row and the book).
  useEffect(() => {
    const book_2 = bookRef.current;
    const row_0 = book_2?.querySelector<HTMLElement>("#row");
    const bar = book_2?.querySelector<HTMLElement>(".pips");
    if (!book_2 || !row_0 || !bar) return;
    const ro = new ResizeObserver(() => bar.toggleAttribute("data-fits", book_2.scrollWidth <= book_2.clientWidth + 1));
    ro.observe(book_2);
    ro.observe(row_0);
    return () => ro.disconnect();
  }, [bookRef]);
  const go = (i_2: number) => {
    const book_3 = bookRef.current;
    const s_4 = book_3?.querySelectorAll<HTMLElement>("#row > section:not(.fill)")[i_2];
    if (!book_3 || !s_4) return;
    // centred (snapping settles it on its edge); the nav: all the way left
    book_3.scrollTo({
      left: i_2 === 0 ? 0 : s_4.offsetLeft - (book_3.clientWidth - s_4.offsetWidth) / 2,
      behavior: "smooth"
    });
  };
  return <main id="book" data-t="book" ref={bookRef}>
			{/* back to the nav: a < bottom-left, shown by CSS (book.css .to-nav) once the nav has scrolled out */}
			<button type="button" className="to-nav" data-t="to-nav" aria-label={t("nav.toNav")} onClick={() => bookRef.current?.scrollTo({
      left: 0,
      behavior: "smooth"
    })}>
				<Icon name="back" />
			</button>
			{/* where you are: = the nav, a dot per page, a capsule for each page in view (book.css .pips) */}
			<div className="pips" data-t="pips">
				{pips.map((p_0, i_3) => <button key={p_0 || i_3} type="button" className={p_0 === "nav" ? "pip nav" : "pip"} style={{
        "--t": `--p${i_3}`
      } as CSSProperties} aria-label={p_0 === "nav" ? t("nav.toNav") : p_0} onClick={() => go(i_3)} />)}
			</div>
			{overlay}
			<div id="row">
				{children}
				<section className="fill" aria-hidden />
			</div>
		</main>;
}